import { createEffect } from 'effector';
import { useUnit } from 'effector-react';
import { isEqual } from 'lodash';
import $$api from 'models/api';
import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';

import CommunityTab from 'components/StyledTemplates/CommunityTab';
import PopUpWrapper from 'components/StyledTemplates/PopUpWrapper';
import { Button } from 'components/StyledTemplates/StyledTemplates';

import ModalsService from 'services/ModalsService';
import createFxPendingsStore from 'utils/factories/create-fx-pendings-store';

import useModal from 'hooks/useModal';
import useResources from 'hooks/useResources';

import imgIconsDownloadDocuments from 'assets/icons/download-documents.png';

import { type Document } from 'types/zod/document';

const deleteScanFx = createEffect(async (args: { id: number; filename: string }) => {
    await $$api.documents.deleteScanFx(args);
    return await $$api.participants.getCommunityFx(undefined);
});

const $downloadScanPendings = createFxPendingsStore($$api.documents.downloadScanFx);
const $deleteScanPendings = createFxPendingsStore(deleteScanFx);

const DashboardScansModal = () => {
    const { t } = useResources();
    const [selectedDocument, setSelectedDocument] = useState<Document | undefined>(undefined);

    const downloadScanPendings = useUnit($downloadScanPendings);
    const deleteScanPendings = useUnit($deleteScanPendings);

    useModal('DASHBOARD_SCANS', {
        onOpen: setSelectedDocument,
    });

    useEffect(() => {
        if (!selectedDocument?.scan_names?.length) {
            handlers.closeModal();
        }
    }, [selectedDocument]);
    const handlers = {
        closeModal: () => {
            ModalsService.hideModal(`DASHBOARD_SCANS`);
        },
        downloadScan: (filename: string) => async () => {
            if (selectedDocument) {
                await $$api.documents.downloadScanFx({ documentId: selectedDocument.document_id, filename });
            }
        },
        deleteScan: (filename: string) => async () => {
            if (selectedDocument) {
                const { docs: newDocs } = await deleteScanFx({ id: selectedDocument?.document_id, filename });
                setSelectedDocument(newDocs.find?.((d) => d.document_id === selectedDocument.document_id));
            }
        },
    };
    return (
        <PopUpWrapper name="DASHBOARD_SCANS" whiteCross size="lg">
            <CommunityTab title={<h5 className="modal-title">{t('newDashboardPage.scansModal.title')}</h5>}>
                <div className="d-flex flex-column gap-2 w-full mb-3">
                    {selectedDocument?.scan_names?.map?.((scanName, index) => {
                        const isDownloadingPending = downloadScanPendings.some((args) =>
                            isEqual(args, { documentId: selectedDocument?.document_id, filename: scanName }),
                        );
                        const isDeletingPending = deleteScanPendings.some((args) =>
                            isEqual(args, { id: selectedDocument?.document_id, filename: scanName }),
                        );
                        return (
                            <div key={index} className="d-flex justify-content-between w-100">
                                <button
                                    data-qaid="download_scan_button"
                                    className="btn btn-link text-dark d-flex gap-2 align-items-center"
                                    onClick={handlers.downloadScan(scanName)}
                                    disabled={isDownloadingPending}
                                >
                                    {isDownloadingPending ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <>
                                            <img alt="Download" src={imgIconsDownloadDocuments} className="w-4" />
                                            <span>{scanName}</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    data-qaid="delete_scan_button"
                                    className="btn btn-danger h-fit"
                                    onClick={handlers.deleteScan(scanName)}
                                    disabled={isDeletingPending}
                                >
                                    {isDeletingPending ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <span className="truncate">{t('newDashboardPage.scansModal.delete')}</span>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
                <div className="d-flex justify-content-end w-100">
                    <Button data-qaid="close_button" className="btn btn-secondary" onClick={handlers.closeModal}>
                        {t('newDashboardPage.scansModal.close')}
                    </Button>
                </div>
            </CommunityTab>
        </PopUpWrapper>
    );
};

export default DashboardScansModal;
